/**
 * Zod schemas for admin_actions audit table + the AdminAction enum.
 *
 * Spec authority: `.plan/features/2026-04-24-admin-beta-feedback.md §4.2 + §4.3`
 * Architecture ref: `architecture.md §4.7` (D1 global schema additions).
 *
 * admin_email is denormalised on purpose so a future transition from
 * ADMIN_EMAIL-secret to a proper admin_users table does not require backfilling.
 *
 * Pure TS + Zod — no fs / node / DOM imports (package purity invariant).
 */

import { z } from "zod";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

// ── AdminAction enum ──────────────────────────────────────────────────────────

export const AdminActionSchema = z.enum([
  "waitlist.approve",
  "waitlist.reject",
  "invite.mint",
  "invite.revoke",
  "invite.resend",
  "feedback.retry_dispatch",
]);

export type AdminAction = z.infer<typeof AdminActionSchema>;

// ── AdminActionRow (DB row shape) ─────────────────────────────────────────────

export const AdminActionRowSchema = z
  .object({
    uuid,
    admin_email: z.string().email(),
    action: AdminActionSchema,
    target_uuid: uuid.nullable().optional(),
    payload_json: z.string().nullable().optional(),
    created_at: timestamp,
  })
  .strict();

export type AdminActionRow = z.infer<typeof AdminActionRowSchema>;
